<template>
	<li
		v-if="!isShippingLineItem"
		class="relative"
		:class="{
			'relative py-6 border-b flex': true
		}"
	>
		<nuxt-link
			:to="routeToProduct"
			class="shrink-0 w-[7.5rem] overflow-hidden mr-4 lg:mr-6 block"
			:aria-label="itemRef?.Name || ''"
			@click="handleNavigate"
		>
			<nuxt-img
				:src="itemImage"
				:alt="itemRef?.Name || ''"
				sizes="120px"
				class="h-full w-full object-contain object-center"
				:provider="itemRef.ImageKey || imageUrl ? undefined : 'storyblok'"
			/>
		</nuxt-link>
		<div class="flex-1 min-w-0">
			<nuxt-link
				:to="routeToProduct"
				class="mb-1 block hover:underline"
				:data-testid="variantLabel ? 'cart-item-variant' : undefined"
				@click="handleNavigate"
			>
				{{ displayName }}
			</nuxt-link>
			<div class="gap-2 text-sm mb-4 flex">
				<Price
					:old-price="oldPriceTotal"
					:price="currentPriceTotal"
					:show-previous="false"
					:show-save-text="hasDiscount"
				/>
			</div>

			<template v-if="itemType === 'product-minicart'">
				<ProductProductAttributesAddToCartQuantitySelector
					v-model="quantity"
					class="!h-[38px] !w-[108px] mt-4"
					:min="1"
					:max="maxQuantity"
					:disabled="getIsLoading"
					@update:modelValue="updateItem()"
				/>
			</template>
		</div>
		<TrashIcon
			v-if="itemType === 'product-minicart'"
			class="shrink-0 cursor-pointer text-rimary ml-auto h-6 w-6"
			@click="removeItemFromCart"
		/>
	</li>
</template>

<script setup lang="ts">
import { TrashIcon } from "@heroicons/vue/24/outline";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import Price from "../Product/ProductAttributes/Price.vue";
import { useGtmEvents } from "~/templates/breadandboxers/composables/useGtmEvents";
import { isShippingItem } from "~/utils/cart/isShippingItem";
import { resolveVariantLabel } from "~/templates/breadandboxers/utils/variantLabel";
import { useLineSingleUnitPrice } from "~/templates/breadandboxers/composables/useLineSingleUnitPrice";
import { resolveLineReferencePrice } from "~/templates/breadandboxers/domain/pricing";

interface Props {
	lineItem: Partial<BasketItem>;
	itemType: string;
	imageUrl?: string;
}

const { lineItem, itemType, imageUrl } = defineProps<Props>();

const config = useRuntimeConfig();
const cartStore = useCartStore();
const { getIsLoading } = storeToRefs(useHelpersStore());
const localePath = useLocalePath();
const { addToCart, removeFromCart } = useGtmEvents();

// Side-cart open ref is provided by LayoutSideCart. Used to close the
// drawer when the user clicks through to the PDP from the line item.
const sidebarOpenRef = inject<Ref<boolean> | null>("isSidebarOpen", null);

const ImagesCdnUrl = config.public.norce.cdn;
const itemRef = toRef(() => lineItem);
const isShippingLineItem = computed(() => isShippingItem(itemRef.value));

const productStore = useProductStore();
const itemImage = computed(() => {
	if (imageUrl) {
		return imageUrl;
	} else
		return imageUrl
			? `${ImagesCdnUrl}/${imageUrl}?format=webp&w=120`
			: lineItem?.ImageKey
				? `${ImagesCdnUrl}/${lineItem.ImageKey}?format=webp&w=120`
				: productStore.defaultProductImage;
});
const routeToProduct = computed(() =>
	localePath(`/${itemRef.value?.UniqueName}`)
);

// Variant label (size / color) comes from `BasketItem.Info[]`, which AddToCart
// writes from the selected parametrics — the same source the product's
// `VariantParametrics` would give, minus a request. This used to fetch the
// whole product per cart line just to read those two values, and
// `get-product-by-unique-name` is the one product endpoint without a Redis
// cache, so a five-line cart cost five uncached Norce calls per render.
//
// No fallback on purpose: `SubHeader` is the product's subtitle (rendered on the
// PDP and used as the meta description), not variant identity, so using it here
// would print an unrelated sentence next to the product name. If the info types
// aren't configured for a deploy (`NORCE_BASKET_ITEM_INFO_TYPES`), the line
// simply shows no variant.
const variantLabel = computed(() =>
	resolveVariantLabel(itemRef.value, cartStore.basket?.Items)
);
const displayName = computed(() => {
	const name = itemRef.value?.Name ?? "";
	return variantLabel.value ? `${name} - ${variantLabel.value}` : name;
});

// Volume-discount aware pricing for the cart row. The line totals
// (price × quantity) make the discount visible across multiple units.
// The price this line was reduced from: `PriceOriginal`, then `PriceStandard`,
// then — for a quantity break, which Norce prices into all three fields — what
// one unit costs on its own. `PriceRecommended` is deliberately not among
// them: it is the merchant's recommended price, never calculated and never
// charged, so striking it through announces a reduction that did not happen.
// See `resolveLineReferencePrice`.
const quantityValue = computed(() => itemRef.value?.Quantity ?? 1);
const currentPriceTotal = computed(
	() => (itemRef.value?.PriceDisplayIncVat ?? 0) * quantityValue.value
);
const { singleUnitPrice } = useLineSingleUnitPrice(itemRef);
const referenceOldPrice = computed<number | null>(() =>
	resolveLineReferencePrice({
		unitPrice: itemRef.value?.PriceDisplayIncVat,
		original: itemRef.value?.PriceOriginalIncVat,
		standard: itemRef.value?.PriceStandardIncVat,
		singleUnitPrice: singleUnitPrice.value
	})
);
const oldPriceTotal = computed<number | null>(() => {
	const original = referenceOldPrice.value;
	if (original == null) return null;
	return original * quantityValue.value;
});
const hasDiscount = computed(() => oldPriceTotal.value !== null);

const quantity = ref<number>(itemRef.value?.Quantity ?? 0);
// The cap for increasing quantity from the cart comes with the basket line
// itself (`OnHandValue`, mirrored by `OnHand.Value`) — no request needed.
const maxQuantity = computed(
	() => itemRef.value.OnHandValue ?? itemRef.value.OnHand?.Value ?? 0
);
const updateItem = async () => {
	const previousQuantity = itemRef.value.Quantity ?? 0;
	const delta = quantity.value - previousQuantity;
	if (delta < 0) {
		removeFromCart({ ...itemRef.value, Quantity: Math.abs(delta) });
	} else if (delta > 0) {
		addToCart({ ...itemRef.value, Quantity: delta });
	}
	cartStore?.updateCartItem(
		itemRef.value.LineNo!,
		itemRef.value.PartNo!,
		quantity.value!
	);
};

const handleNavigate = () => {
	if (sidebarOpenRef) {
		sidebarOpenRef.value = false;
	}
};

const removeItemFromCart = () => {
	removeFromCart(itemRef.value);
	cartStore?.removeFromCart(itemRef.value.LineNo!);
};

watch(itemRef, () => {
	quantity.value = itemRef.value.Quantity ?? 0;
});
</script>
